/**
 * Theme Sync Component
 * Synchronizes theme state between ThemeContext and user preferences
 */

import { useAuth } from '@contexts/AuthContext';
import { useTheme } from '@contexts/ThemeContext';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { DEFAULT_THEME_ID } from '@parlay-picker/shared/themes';
import { type ReactElement, type ReactNode, useEffect, useRef } from 'react';

interface ThemeSyncProps {
  children: ReactNode;
}

export function ThemeSync({ children }: ThemeSyncProps): ReactElement {
  const { themeId, setTheme } = useTheme();
  const { user, updatePreferences, isAuthenticated } = useAuth();
  const { canUseTheme } = useFeatureAccess();
  const hasLoadedFromServer = useRef(false);

  // Load theme from user preferences ONCE when user first logs in
  useEffect(() => {
    if (isAuthenticated && user?.preferences?.theme && !hasLoadedFromServer.current) {
      hasLoadedFromServer.current = true;
      if (user.preferences.theme !== themeId) {
        setTheme(user.preferences.theme);
      }
    }
    // Reset flag when user logs out
    if (!isAuthenticated) {
      hasLoadedFromServer.current = false;
    }
  }, [isAuthenticated, user?.preferences?.theme, themeId, setTheme]);

  // Reset premium theme if user's tier no longer allows it
  useEffect(() => {
    if (isAuthenticated && hasLoadedFromServer.current && !canUseTheme(themeId)) {
      setTheme(DEFAULT_THEME_ID);
    }
  }, [isAuthenticated, themeId, canUseTheme, setTheme]);

  // Save theme to user preferences when it changes (if authenticated)
  useEffect(() => {
    // Only save if we've already loaded from server (prevents overwriting on initial load)
    if (isAuthenticated && hasLoadedFromServer.current && user?.preferences?.theme !== themeId) {
      // Debounce to avoid excessive API calls
      const timeoutId = setTimeout(() => {
        updatePreferences({ theme: themeId }).catch((_error) => {});
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [themeId, isAuthenticated, user?.preferences?.theme, updatePreferences]);

  return <>{children}</>;
}
